"use client";

import { Button } from "@repo/ui";
import { Badge } from "@repo/ui";
import { Bell, BellRing } from "lucide-react";
import { type ComponentProps, forwardRef } from "react";

import { cn } from "@/lib/utils";

interface NotificationIconProps extends ComponentProps<typeof Button> {
  unreadCount?: number;
  /**
   * Caption under the bell. For surfaces where the bell sits among labelled
   * controls -- the sidebar rail -- and a bare glyph would read as an ornament
   * rather than something to press.
   */
  label?: string;
}

/**
 * Forwards its ref and the rest of its props to the button so it can be a menu
 * trigger directly. `NotificationDropdown` needs that: a wrapper element around
 * the button takes the trigger props but cannot hold focus.
 */
export const NotificationIcon = forwardRef<
  HTMLButtonElement,
  NotificationIconProps
>(function NotificationIcon(
  { unreadCount = 0, className, label, ...props },
  ref,
) {
  const BellIcon = unreadCount > 0 ? BellRing : Bell;

  return (
    <Button
      ref={ref}
      variant="outline"
      {...props}
      className={cn(
        "border-border text-muted-foreground hover:border-primary hover:bg-accent hover:text-foreground relative size-8 rounded-full p-0 shadow-none transition-colors",
        label && "size-auto flex-col gap-1.5 rounded-lg",
        className,
      )}
    >
      {/* `Button` sizes its own descendant svgs via `[&_svg]:size-4`, which
          outranks a plain utility on the icon -- hence the important. */}
      <BellIcon className={label ? "size-5!" : "size-4"} />
      {label && <span className="block w-full truncate">{label}</span>}
      {unreadCount > 0 && (
        <Badge
          variant="secondary"
          className={cn(
            "absolute flex size-4 items-center justify-center rounded-full bg-blue-500 p-0 text-[10px] font-medium text-white hover:bg-blue-600",
            // Tucked inside the tile once there is one, the way the nav rows
            // carry their own counts; hung off the edge of a bare icon.
            label ? "top-1 right-2" : "-top-1 -right-1",
          )}
        >
          {unreadCount > 9 ? "9+" : unreadCount}
        </Badge>
      )}
    </Button>
  );
});
